import React, { useCallback, useState } from 'react';
import Button from '@mui/material/Button';
import styled from 'styled-components';
import {
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Tooltip,
  LinearProgress,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Chip,
} from '@mui/material';
import { useIndicatorServices } from 'shared/apollo';

import GlobalResourceDropDown from 'shared/account/components/GlobalResourceDropDown';
import Alert from '@mui/lab/Alert';
import ViewSidebar from '@mui/icons-material/ViewSidebar';
import { Add as AddIcon } from '@mui/icons-material';
import SearchInput from 'shared/components/SearchInput';
import CheckboxOriginal from 'shared/components/Checkbox';
import { distance } from 'fastest-levenshtein';
import get from 'lodash/get';
import { GroupsContainer, PageTop } from './styled';
const itemsPerPage = 100;
// Inversly proportional to levenshtien distance for search filter
const matchingFrac = 3;
const GlobalResource = (
  (
    {
      resource,
      fields,
      fieldTitles,
      isLoading,
      onAddNew,
      handleViewDetails,
      data,
      // onMerge,
      handleDelete,
    }
  ) => {
    const [filter, setFilter] = useState();
    const [selection, setSelection] = useState([]);
    const items = data
      ?.filter(obj => !filter || matchQuickFilter(obj, filter));
    function matchQuickFilter(obj, filter) {
      filter = filter.toLowerCase();
      return fields.slice(0, 2)
        .some(field => String(get(obj, field)).toLowerCase().includes(filter)
          || distance(String(get(obj, field)).substring(0, filter.length).toLowerCase()
            .replace(/\s+/g, ''), filter.toLowerCase().replace(/\s+/g, '')) <= filter.length / matchingFrac);
    }
    const handleSearchInput = useCallback(event => {
      setFilter(event.target.value.toLowerCase());
    }, []);
    const [selectAll, setSelectAll] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const [selectedImpactModel, setselectedImpactModel] = useState([]);

    const { data: services } = useIndicatorServices();

    const handleChangeImpactModelFilter = useCallback(event => {
      setselectedImpactModel(event.target.value);
    });


    const impactModelOptions = Array.from(
      new Set(
        services?.map(({ name }) => name)
      )
    ).sort();

    let paginatedItems = items?.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    ).filter(item => {
      return selectedImpactModel.length === 0 ||
        selectedImpactModel.some(option => item.services.map(service => service.name).includes(option));
    });
    if (filter) {
      paginatedItems = paginatedItems.sort((a, b) => Math.min(
        distance((a.dbId).toString(), filter),
        distance(a.name, filter)
      ) - Math.min(distance((b.dbId).toString(), filter), distance(b.name, filter)));
    }
    const totalPages = Math.ceil((items?.length || 0) / itemsPerPage);
    const handlePageChange = page => {
      setCurrentPage(page);
    };
    const generatePageNumbers = (totalPages, currentPage, pageWindow = 5) => {
      const startPage = Math.max(1, currentPage - Math.floor(pageWindow / 2));
      const endPage = Math.min(totalPages, startPage + pageWindow - 1);

      const pages = [];
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      return pages;
    };
    const PaginationControls = () => {
      const pages = generatePageNumbers(totalPages, currentPage);

      return (
        <PaginationContainer>
          {currentPage > 1 && (
            <>
              <PaginationButton onClick={() => handlePageChange(1)}>
                first
              </PaginationButton>
              <PaginationButton onClick={() => handlePageChange(currentPage - 1)}>
                {'<'}
              </PaginationButton>
            </>
          )}
          {pages.map(page => (
            <PaginationButton
              key={page}
              onClick={() => handlePageChange(page)}
              active={page === currentPage}
            >
              {page}
            </PaginationButton>
          ))}
          {currentPage < totalPages && (
            <>
              <PaginationButton onClick={() => handlePageChange(currentPage + 1)}>
                {'>'}
              </PaginationButton>
              <PaginationButton onClick={() => handlePageChange(totalPages)}>
                last
              </PaginationButton>
            </>
          )}
        </PaginationContainer>
      );
    };
    const handleSelectAll = useCallback(() => {
      if (selectAll) {
        setSelection([]);
      } else {
        setSelection(paginatedItems);
      }
      setSelectAll(!selectAll);
    });

    const getField = useCallback((row, field) => {
      if (field !== 'value') return row[field];
      const text = row[field];
      try {
        const formattedText = text.include('\'') ? text.replace(/'/g, '\'') : text;
        const parsed = JSON.parse(formattedText);
        return parsed?.value ?? text;
      } catch {
        return text;
      }
    }, []);

    const handleChangeCheckbox = (row) => {
      if (selection.some(item => item.dbId === row.dbId)) {
        setSelection(selection.filter(item => item.dbId !== row.dbId));
        setSelectAll(false);
      } else {
        setSelection([...selection, row]);
        if (selection.length + 1 == paginatedItems.length) {
          setSelectAll(true);
        }
      }
    };
    const indeterminate = !selectAll && selection.length > 0;

    const FilterDropdown = (
      {
        options,
        selectedOptions,
        handleDropdownChange,
        title
      }
    ) => {
      return (
        <FormControl style={{ maxWidth: '40%', marginRight: '10px' }}>
          {title}
          <Select
            style={{ flexWrap: 'wrap' }}
            renderValue={(selected) => (
              <Box >
                {selected.map((value) => (
                  <Chip key={value} label={value} sx={{ mr: '1' }} />
                ))}
              </Box>
            )}
            multiple
            value={selectedOptions}
            onChange={handleDropdownChange}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 500,
                  maxWidth: 400,
                },
              },
            }}
            input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
          >
            {options.map(option => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={selectedOptions.includes(option)} />
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>

        </FormControl>
      );
    };

    return (
      <HomePage noPadding fullHeight>
        <PageTop>
          {isLoading && <LinearProgress variant="query" />}
          <Grid
            container
            direction="row"
            alignItems="center"
          >
          </Grid>
          <GlobalResourceDropDown value={resource === 'Cause Category' ? 'Cause Categories' : resource + 's'} />
        </PageTop>
        <Root>
          <GroupsContainer>
            <HomePage>
              <Header>
                <HeaderItem>
                  <SearchInput onInput={handleSearchInput}></SearchInput>
                </HeaderItem>
                {/* {categoryOptions && <FilterDropdown
                  options={categoryOptions}
                  selectedOptions={selectedCategory}
                  handleDropdownChange={handleChangeCategoryFilter}
                  title="Filter by Cause Category" />} */}
                {resource == 'Indicator' && impactModelOptions && <FilterDropdown
                  options={impactModelOptions}
                  selectedOptions={selectedImpactModel}
                  handleDropdownChange={handleChangeImpactModelFilter}
                  title="Filter by Impact Model" />}
                <LeftHeader>
                  {handleDelete && selection.length > 0 && (
                    <MainButton onClick={() => {
                      handleDelete(selection);
                      setSelection([]);
                      setSelectAll(false);
                    }}>
                      Delete
                    </MainButton>
                  )}
                  {/* {onMerge && selection.length > 1 &&
                    (<MainButton onClick={() => {
                      onMerge(selection)
                      setSelection([])
                      setSelectAll(false)
                    }}>
                      Merge
                    </MainButton>
                    )} */}
                </LeftHeader>

                <HeaderItem
                  display='flex'
                  justifyContent="flex-end">
                  <MainButton

                    onClick={(onAddNew)}
                    startIcon={<AddIcon />}
                  >
                    Add {resource}
                  </MainButton>

                </HeaderItem>
              </Header>
              &nbsp;&nbsp;
              {isLoading && <div>Loading...</div>}
            </HomePage>
            {!isLoading && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Tooltip
                          title={selectAll ? 'Deselect All' : 'Select All'}>
                          <CheckboxOriginal
                            checked={selectAll}
                            onClick={handleSelectAll}
                            indeterminate={indeterminate}
                          >
                          </CheckboxOriginal>
                        </Tooltip>
                      </TableCell>
                      {fieldTitles.map(field => (
                        <TableCell key={field}>{field}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedItems?.map(row => (
                      <TableRow
                        key={row}
                        hover
                        selected={selectAll || selection.some(item => item.dbId === row.dbId)}
                      >
                        <TableCell onClick={() => { handleChangeCheckbox(row); }}>
                          <CheckboxOriginal
                            checked={selectAll || selection.some(item => item.dbId === row.dbId)}
                          />
                        </TableCell>
                        {fields.map(field => (
                          <TableCell key={field} onClick={() => { handleChangeCheckbox(row); }}>{getField(row, field)}</TableCell>
                        ))}
                        <TableCell >
                          <MainButton
                            onClick={() => { handleViewDetails(row); }}
                            endIcon={<ViewSidebar />}
                          >
                            View Details
                          </MainButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {!isLoading && totalPages > 1 && <PaginationControls />}
                {items.length === 0 && <Alert severity="infor"> No Items Found</Alert>}
              </TableContainer>
            )}
          </GroupsContainer>
        </Root>
      </HomePage>
    );
  }
);

export default GlobalResource;

const HomePage = styled.div`
  flex: 1 1 auto;
  ${({ noPadding }) => !noPadding && 'padding: 10px 20px;'}
  ${({ fullHeight }) => fullHeight && 'height: calc(100vh - var(--app-header-height)); display: flex; flex-direction: column;'}

  div.MuiPaper-root {
    button {
      .MuiButton-label {
        color: #ffffffff !important;
      }
    }
  }
`;

const MainButton = styled(Button).attrs({
  variant: 'contained',
  color: 'primary',
})`
  text-transform: none !important;
  float: left;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderItem = styled.div`
  margin-right: 10px;
`;

const LeftHeader = styled.div`
  margin-right: auto;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const PaginationButton = styled.button`
  color: #0073a3;
  margin: 0 5px;
  border: none;
  background: none;
  cursor: pointer;
  text-decoration: ${({ active }) => (active ? 'underline' : 'none')};
`;

const Root = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
`;